import { ReaderStudy } from "./ReaderStudy";
import {
  ArrowLeft,
  ArrowRight,
  BookOpenText,
  BookmarkSimple,
  CaretDown,
  Check,
  DownloadSimple,
  Info,
  List,
  MagnifyingGlass,
  MapTrifold,
  NotePencil,
  Pause,
  Play,
  SpeakerHigh,
  Sparkle,
  Stop,
  Trash,
  WarningCircle,
  X,
} from "@phosphor-icons/react";
import {
  type FormEvent,
  type PointerEvent as ReactPointerEvent,
  type UIEvent as ReactUIEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { brand } from "../config/brand";
import { api } from "../lib/api";
import {
  bibleBooks,
  formatDownloadSize,
  installBibleVersion,
  installedBibleVersions,
  type BibleData,
  loadBibleVersion,
  loadKjv,
  parseReference,
  referencePath,
  removeBibleVersion,
} from "../lib/bible";
import {
  loadPassageSections,
  passageSectionLabel,
  searchPassageSections,
  sectionsStartingInChapter,
  type BiblePassageSection,
} from "../lib/bible-sections";
import {
  bibleVersion,
  bibleVersionCatalog,
  defaultBibleVersionId,
  isBibleVersionId,
  normalizeScriptureRanges,
  scriptureRangeContains,
  scriptureRangeLabel,
  scriptureSelectionVerseCount,
  toggleScriptureVerse,
  type BibleAudioManifest,
  type BibleAudioSegment,
  type BibleVersionId,
  type BibleVersionManifest,
  type ScriptureRefRange,
  type ScriptureSelection,
  type WordTiming,
} from "../shared/bible";
import {
  parseKjvEditorialVerse,
  readableBibleText,
  type BibleEditorialNote,
} from "../shared/bible-editorial";
import {
  bookmarkMatchesLocation,
  bookmarkedBooks,
  bookmarkedChapters,
  scriptureBookmarkPath,
  type ScriptureBookmark,
} from "../shared/bookmarks";

interface ScriptureReaderProps {
  user: { id: string } | null;
  profile: { bible_translation: string } | null;
  onAuth: () => void;
  onOpenNavigation: () => void;
}

interface Annotation {
  id: string;
  verseStart: number;
  verseEnd: number;
  highlightColor: "yellow" | "green" | "blue" | "pink" | "purple" | null;
  note: string | null;
  tagsJson?: string;
  notebookId?: string | null;
  isBookmarked: number;
}

interface ChapterResponse {
  versionId: BibleVersionId;
  editionHash: string;
  abbreviation: string;
  attribution: string;
  book: string;
  bookIndex: number;
  chapter: number;
  verses: string[];
}

const VERSION_STORAGE_KEY = "bible-alone-preferred-version";
const SELECTION_STORAGE_KEY = "bible-alone-scripture-selection";
const AUDIO_SPEEDS = [0.75, 1, 1.25, 1.5, 2] as const;
const QUESTION_SUGGESTIONS = [
  "Explain these passages in context.",
  "How do these passages connect?",
  "Compare responsible Christian interpretations.",
  "What themes do these passages share?",
] as const;

function storedVersion(profile: ScriptureReaderProps["profile"]): BibleVersionId {
  const value =
    (typeof window !== "undefined" && window.localStorage.getItem(VERSION_STORAGE_KEY)) ||
    profile?.bible_translation ||
    defaultBibleVersionId;
  return isBibleVersionId(value) ? value : defaultBibleVersionId;
}

function storedRanges(): ScriptureRefRange[] {
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(SELECTION_STORAGE_KEY) ?? "[]");
    return Array.isArray(parsed) ? normalizeScriptureRanges(parsed as ScriptureRefRange[]) : [];
  } catch {
    return [];
  }
}

function nextChapterReference(bookIndex: number, chapter: number, bible: BibleData | null) {
  const chapterCount = bible?.[bookIndex]?.length ?? 1;
  if (chapter < chapterCount) return { bookIndex, chapter: chapter + 1 };
  if (bookIndex < bibleBooks.length - 1) return { bookIndex: bookIndex + 1, chapter: 1 };
  return null;
}

function previousChapterReference(bookIndex: number, chapter: number, bible: BibleData | null) {
  if (chapter > 1) return { bookIndex, chapter: chapter - 1 };
  if (bookIndex <= 0) return null;
  return {
    bookIndex: bookIndex - 1,
    chapter: bible?.[bookIndex - 1]?.length ?? 1,
  };
}

function bookOptionLabel(book: string, chapterCount: number) {
  return `${book}, ${chapterCount} chapter${chapterCount === 1 ? "" : "s"}`;
}

function versionAvailabilityText(version: BibleVersionManifest, installed: boolean) {
  if (version.available === false) {
    return version.availabilityMessage ?? `${version.abbreviation} is unavailable.`;
  }
  if (version.bundled) return "Included";
  if (installed) return "Installed for offline reading";
  return `${formatDownloadSize(version.sizeBytes)} download`;
}

export function ScriptureReader({
  user,
  profile,
  onAuth,
  onOpenNavigation,
}: ScriptureReaderProps) {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const bookName = decodeURIComponent(params.book ?? "John");
  const bookIndex = Math.max(0, bibleBooks.findIndex((book) => book === bookName));
  const chapterNumber = Math.max(1, Number(params.chapter) || 3);
  const query = new URLSearchParams(location.search);
  const verseParam = query.get("verse");
  const versionParam = query.get("version");

  const [structureBible, setStructureBible] = useState<BibleData | null>(null);
  const [activeBible, setActiveBible] = useState<BibleData | null>(null);
  const [versions, setVersions] = useState<BibleVersionManifest[]>(bibleVersionCatalog);
  const [preferredVersionId, setPreferredVersionId] = useState<BibleVersionId>(() => storedVersion(profile));
  const [activeVersionId, setActiveVersionId] = useState<BibleVersionId>("kjv");
  const [installed, setInstalled] = useState<BibleVersionId[]>(["kjv"]);
  const [downloadProgress, setDownloadProgress] = useState<Partial<Record<BibleVersionId, number>>>({});
  const [versionPickerOpen, setVersionPickerOpen] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [notice, setNotice] = useState("");
  const [search, setSearch] = useState("");
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [booksOpen, setBooksOpen] = useState(false);
  const [passageSections, setPassageSections] = useState<BiblePassageSection[]>([]);
  const [passageGuideResults, setPassageGuideResults] = useState<BiblePassageSection[] | null>(null);
  const [passageGuideQuery, setPassageGuideQuery] = useState("");
  const [editorialNote, setEditorialNote] = useState<{
    reference: string;
    notes: BibleEditorialNote[];
  } | null>(null);

  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [bookmarks, setBookmarks] = useState<ScriptureBookmark[]>([]);
  const [bookmarksOpen, setBookmarksOpen] = useState(false);
  const [readingVerse, setReadingVerse] = useState(() => {
    const requested = Number(verseParam);
    return Number.isInteger(requested) && requested > 0 ? requested : 1;
  });
  const [selectedVerse, setSelectedVerse] = useState<number | null>(null);
  const [note, setNote] = useState("");
  const [tags, setTags] = useState("");
  const [studyOpen, setStudyOpen] = useState(false);
  const studyPanelRef=useRef<HTMLElement>(null);
  const [compactStudy,setCompactStudy]=useState(()=>typeof window!=='undefined'&&window.matchMedia('(max-width:1099px)').matches);
  useEffect(()=>{
    const media=window.matchMedia('(max-width:1099px)');
    const update=()=>setCompactStudy(media.matches);media.addEventListener('change',update);
    return ()=>media.removeEventListener('change',update);
  },[]);
  useEffect(()=>{
    if(!studyOpen||!compactStudy)return;
    const previous=document.activeElement as HTMLElement|null;
    studyPanelRef.current?.querySelector<HTMLButtonElement>('.bible-ai-close')?.focus();
    const key=(event:KeyboardEvent)=>{
      if(event.key==='Escape'&&!event.defaultPrevented){event.preventDefault();setStudyOpen(false);return;}
      if(event.key!=='Tab')return;
      const elements=Array.from(studyPanelRef.current?.querySelectorAll<HTMLElement>('button:not([disabled]),a[href],input,textarea,select,summary,[tabindex="0"]')??[]).filter(element=>element.getClientRects().length>0&&!element.closest('[hidden]')&&element.tabIndex>=0);
      const first=elements[0],last=elements.at(-1);
      if(event.shiftKey&&document.activeElement===first){event.preventDefault();last?.focus();}
      else if(!event.shiftKey&&document.activeElement===last){event.preventDefault();first?.focus();}
    };
    document.addEventListener('keydown',key);
    return ()=>{document.removeEventListener('keydown',key);previous?.focus();};
  },[studyOpen,compactStudy]);


  const [ranges, setRanges] = useState<ScriptureRefRange[]>(storedRanges);
  const [selectionMode, setSelectionMode] = useState(() => storedRanges().length > 0);
  const [questionOpen, setQuestionOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const longPressTimer = useRef<number | null>(null);
  const longPressTriggered = useRef(false);
  const lastSelectedRef = useRef<ScriptureRefRange | null>(null);

  const [audioManifest, setAudioManifest] = useState<BibleAudioManifest | null>(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioPreparing, setAudioPreparing] = useState(false);
  const [audioSpeed, setAudioSpeed] = useState<(typeof AUDIO_SPEEDS)[number]>(1);
  const [activeVerse, setActiveVerse] = useState<number | null>(null);
  const [activeWord, setActiveWord] = useState<WordTiming | null>(null);
  const [followText, setFollowText] = useState(true);
  const [audioElapsed, setAudioElapsed] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioSegmentRef = useRef(0);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const speechQueueRef = useRef<Array<{
    book: string;
    bookIndex: number;
    chapter: number;
    verse: number;
    text: string;
  }>>([]);
  const speechIndexRef = useRef(0);
  const playbackLocationRef = useRef({ bookIndex, chapter: chapterNumber });
  const continueAcrossChaptersRef = useRef(false);
  const pendingContinueRef = useRef(false);
  const readerScrollRef = useRef<HTMLDivElement | null>(null);

  const activeVersion = useMemo(
    () => versions.find((version) => version.id === activeVersionId) ?? bibleVersion(activeVersionId),
    [activeVersionId, versions],
  );
  const chapter = activeBible?.[bookIndex]?.[chapterNumber - 1] ?? [];
  const readerStructure = activeBible ?? structureBible;
  const maxChapter = readerStructure?.[bookIndex]?.length ?? 1;
  const selectionCount = scriptureSelectionVerseCount(ranges);
  const bookmarkedBookNames = useMemo(() => bookmarkedBooks(bookmarks), [bookmarks]);
  const bookmarkedChapterNumbers = useMemo(
    () => bookmarkedChapters(bookmarks, bibleBooks[bookIndex]!),
    [bookmarks, bookIndex],
  );
  const currentBookmark = useMemo(
    () =>
      bookmarks.find((bookmark) =>
        bookmarkMatchesLocation(bookmark, {
          translation: activeVersionId,
          book: bibleBooks[bookIndex]!,
          chapter: chapterNumber,
          verse: readingVerse,
        }),
      ) ?? null,
    [bookmarks, activeVersionId, bookIndex, chapterNumber, readingVerse],
  );
  const previousChapter = previousChapterReference(bookIndex, chapterNumber, readerStructure);
  const nextChapter = nextChapterReference(bookIndex, chapterNumber, readerStructure);
  const chapterSections = useMemo(
    () => sectionsStartingInChapter(passageSections, bookIndex, chapterNumber),
    [passageSections, bookIndex, chapterNumber],
  );
  const sectionsByVerse = useMemo(() => {
    const result = new Map<number, BiblePassageSection[]>();
    for (const section of chapterSections) {
      const existing = result.get(section.startVerse) ?? [];
      existing.push(section);
      result.set(section.startVerse, existing);
    }
    return result;
  }, [chapterSections]);
  const currentSelection: ScriptureSelection = {
    versionId: activeVersionId,
    editionHash: activeVersion.editionHash,
    ranges,
  };
  const versionPickerVisible =
    versionPickerOpen || new URLSearchParams(location.search).get("versions") === "true";

  const openVersionPicker = () => {
    setVersionPickerOpen(true);
    const query = new URLSearchParams(location.search);
    query.set("versions", "true");
    navigate(`${location.pathname}?${query.toString()}`, { replace: true });
  };

  const closeVersionPicker = () => {
    setVersionPickerOpen(false);
    const query = new URLSearchParams(location.search);
    query.delete("versions");
    navigate(`${location.pathname}${query.size ? `?${query.toString()}` : ""}`, { replace: true });
  };

  useEffect(() => {
    void Promise.all([
      loadKjv(),
      installedBibleVersions(),
      api<{ defaultVersionId: BibleVersionId; versions: BibleVersionManifest[] }>("/api/bible/versions")
        .catch(() => ({ defaultVersionId: defaultBibleVersionId, versions: bibleVersionCatalog })),
    ]).then(([kjv, installedIds, response]) => {
      setStructureBible(kjv);
      setInstalled(installedIds);
      setVersions(response.versions);
      const requested =
        versionParam && isBibleVersionId(versionParam)
          ? versionParam
          : storedVersion(profile);
      const requestedManifest = response.versions.find((version) => version.id === requested);
      if (
        requestedManifest?.available !== false &&
        (requestedManifest?.bundled || installedIds.includes(requested))
      ) {
        setPreferredVersionId(requested);
        setActiveVersionId(requested);
        if (versionParam && isBibleVersionId(versionParam)) {
          window.localStorage.setItem(VERSION_STORAGE_KEY, requested);
        }
      } else {
        setPreferredVersionId(response.defaultVersionId);
        setActiveVersionId("kjv");
        window.localStorage.setItem(VERSION_STORAGE_KEY, response.defaultVersionId);
        if (
          versionParam &&
          isBibleVersionId(versionParam) &&
          requestedManifest?.downloadable &&
          !installedIds.includes(requested)
        ) {
          setVersionPickerOpen(true);
          setNotice(`Download ${requestedManifest.abbreviation} on this device to reopen this bookmark in its saved edition.`);
        } else if (requestedManifest?.available === false) {
          setNotice(`${requestedManifest.abbreviation} is unavailable. KJV is now your default Bible.`);
          if (user) {
            void api("/api/profile/bible-version", {
              method: "PATCH",
              body: JSON.stringify({ versionId: response.defaultVersionId }),
            }).catch(() => undefined);
          }
        }
      }
    }).catch((error) => setLoadError(error instanceof Error ? error.message : String(error)));
  }, [profile?.bible_translation, user, versionParam]);

  useEffect(() => {
    void loadPassageSections()
      .then((payload) => setPassageSections(payload.sections))
      .catch(() => {
        // Exact-reference navigation remains available if the optional guide is offline.
      });
  }, []);

  useEffect(() => {
    setLoadError("");
    void loadBibleVersion(activeVersionId)
      .then(setActiveBible)
      .catch((error) => setLoadError(error instanceof Error ? error.message : String(error)));
  }, [activeVersionId, bookIndex, chapterNumber]);

  useEffect(() => {
    window.sessionStorage.setItem(SELECTION_STORAGE_KEY, JSON.stringify(ranges));
  }, [ranges]);

  useEffect(() => {
    if (!user) {
      setAnnotations([]);
      return;
    }
    void api<{ annotations: Annotation[] }>(
      `/api/annotations?book=${encodeURIComponent(bibleBooks[bookIndex]!)}&chapter=${chapterNumber}&translation=${activeVersionId}`,
    ).then((response) => setAnnotations(response.annotations)).catch(() => setAnnotations([]));
  }, [user?.id, bookIndex, chapterNumber, activeVersionId]);

  useEffect(() => {
    if (!user) {
      setBookmarks([]);
      return;
    }
    void api<{ bookmarks: ScriptureBookmark[] }>("/api/bookmarks")
      .then((response) => setBookmarks(response.bookmarks))
      .catch(() => setBookmarks([]));
  }, [user?.id]);

  useEffect(() => {
    if (!user) return;
    void api("/api/reading-history", {
      method: "POST",
      body: JSON.stringify({
        translation: activeVersionId,
        book: bibleBooks[bookIndex],
        chapter: chapterNumber,
        verse: verseParam ? Number(verseParam) : null,
      }),
    }).catch(() => undefined);
  }, [user?.id, activeVersionId, bookIndex, chapterNumber, verseParam]);

  useEffect(() => {
    const requested = Number(verseParam);
    if (verseParam && Number.isInteger(requested) && requested > 0) {
      setReadingVerse(requested);
      setSelectedVerse(requested);
      setStudyOpen(true);
      window.setTimeout(
        () => document.getElementById(`verse-${requested}`)?.scrollIntoView({ block: "center" }),
        100,
      );
      return;
    }
    setReadingVerse(1);
    setSelectedVerse(null);
    const frame = window.requestAnimationFrame(() => {
      readerScrollRef.current?.scrollTo({ top: 0, behavior: "auto" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [bookIndex, chapterNumber, verseParam, chapter.length]);

  useEffect(() => {
    const annotation = annotations.find((item) => item.verseStart === selectedVerse);
    setNote(annotation?.note ?? "");
    try {
      const parsed = JSON.parse(annotation?.tagsJson ?? "[]");
      setTags(Array.isArray(parsed) ? parsed.join(", ") : "");
    } catch {
      setTags("");
    }
  }, [annotations, selectedVerse]);

  useEffect(() => {
    setBooksOpen(false);
    setMobileSearchOpen(false);
    setStudyOpen(false);
    if (pendingContinueRef.current) {
      pendingContinueRef.current = false;
      window.setTimeout(() => void startPlayback(true), 120);
    }
  }, [bookIndex, chapterNumber]);

  useEffect(() => {
    if (!followText || activeVerse == null) return;
    const element = document.getElementById(`verse-${activeVerse}`);
    if (!element) return;
    const scroller = element.closest<HTMLElement>(".bible-scroll");
    if (!scroller) return;
    const bounds = element.getBoundingClientRect();
    const scrollBounds = scroller.getBoundingClientRect();
    if (bounds.top < scrollBounds.top + 40 || bounds.bottom > scrollBounds.bottom - 60) {
      element.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  }, [activeVerse, followText]);

  useEffect(
    () => () => {
      audioRef.current?.pause();
      window.speechSynthesis?.cancel();
    },
    [],
  );

  const switchVersion = async (version: BibleVersionManifest) => {
    setLoadError("");
    if (version.available === false) {
      setNotice(version.availabilityMessage ?? `${version.abbreviation} is not available yet.`);
      return;
    }
    if (version.downloadable && !installed.includes(version.id)) {
      try {
        await installBibleVersion(version, (percent) =>
          setDownloadProgress((current) => ({ ...current, [version.id]: percent })),
        );
        setInstalled((current) => [...new Set([...current, version.id])]);
      } catch (error) {
        setLoadError(error instanceof Error ? error.message : "The Bible download failed.");
        return;
      } finally {
        setDownloadProgress((current) => ({ ...current, [version.id]: undefined }));
      }
    }
    setPreferredVersionId(version.id);
    setActiveVersionId(version.id);
    window.localStorage.setItem(VERSION_STORAGE_KEY, version.id);
    if (user) {
      void api("/api/profile/bible-version", {
        method: "PATCH",
        body: JSON.stringify({ versionId: version.id }),
      }).catch(() => setNotice("This device changed versions, but account sync will retry later."));
    }
    closeVersionPicker();
    setNotice("");
  };

  const removeVersion = async (version: BibleVersionManifest) => {
    await removeBibleVersion(version.id);
    setInstalled((current) => current.filter((id) => id !== version.id));
    if (activeVersionId === version.id) {
      setActiveVersionId("kjv");
      setPreferredVersionId("kjv");
      window.localStorage.setItem(VERSION_STORAGE_KEY, "kjv");
    }
  };

  const toggleSelection = (verse: number, event?: { shiftKey?: boolean }) => {
    const current: ScriptureRefRange = {
      book: bibleBooks[bookIndex]!,
      bookIndex,
      chapter: chapterNumber,
      verseStart: verse,
      verseEnd: verse,
    };
    if (
      event?.shiftKey &&
      lastSelectedRef.current?.bookIndex === bookIndex &&
      lastSelectedRef.current.chapter === chapterNumber
    ) {
      const from = Math.min(lastSelectedRef.current.verseStart, verse);
      const to = Math.max(lastSelectedRef.current.verseStart, verse);
      setRanges((existing) =>
        normalizeScriptureRanges([
          ...existing,
          { ...current, verseStart: from, verseEnd: to },
        ]),
      );
    } else {
      setRanges((existing) => toggleScriptureVerse(existing, current));
    }
    lastSelectedRef.current = current;
    setSelectionMode(true);
  };

  const pointerDown = (verse: number) => {
    longPressTriggered.current = false;
    longPressTimer.current = window.setTimeout(() => {
      longPressTriggered.current = true;
      toggleSelection(verse);
    }, 450);
  };

  const cancelLongPress = () => {
    if (longPressTimer.current != null) window.clearTimeout(longPressTimer.current);
    longPressTimer.current = null;
  };

  const verseClick = (verse: number, event: ReactPointerEvent<HTMLButtonElement>) => {
    cancelLongPress();
    if (longPressTriggered.current) return;
    if (selectionMode || event.ctrlKey || event.metaKey || event.shiftKey) {
      toggleSelection(verse, event);
      return;
    }
    setSelectedVerse(verse);
    setStudyOpen(true);
  };

  const clearSelection = () => {
    setRanges([]);
    setSelectionMode(false);
    lastSelectedRef.current = null;
  };

  const resolveRangeVerses = async (range: ScriptureRefRange) => {
    const localChapter = activeBible?.[range.bookIndex]?.[range.chapter - 1];
    if (localChapter?.length) return localChapter;
    const response = await api<ChapterResponse>(
      `/api/bible/text/${activeVersionId}/${encodeURIComponent(range.book)}/${range.chapter}`,
    );
    if (response.editionHash !== activeVersion.editionHash) {
      throw new Error("The selected Bible edition changed. Please reselect these verses.");
    }
    return response.verses;
  };

  const selectedText = async () => {
    const passages = await Promise.all(ranges.map(async (range) => {
      const source = await resolveRangeVerses(range);
      const body = source
        .slice(range.verseStart - 1, range.verseEnd)
        .map((text, index) => `${range.verseStart + index}. ${readableBibleText(text, activeVersionId)}`)
        .join("\n");
      return `${scriptureRangeLabel(range)}\n${body}`;
    }));
    return passages.join("\n\n");
  };

  const askSola = (prompt: string) => {
    if (!ranges.length) return;
    navigate(brand.routes.askAssistant, {
      state: {
        mode: "scripture",
        autoSend: false,
        prompt,
        scriptureSelections: [currentSelection],
      },
    });
  };

  const saveSelectionNote = async () => {
    if (!user) return onAuth();
    const title = ranges.length === 1 ? scriptureRangeLabel(ranges[0]!) : `${selectionCount} selected Scripture verses`;
    const body = await selectedText();
    await api("/api/study/notes", {
      method: "POST",
      body: JSON.stringify({
        source: {
          sourceType: "manual",
          title,
          body: `# ${title}\n\n${body}`,
        },
        tags: ["scripture", ...new Set(ranges.map((range) => range.book.toLowerCase().replace(/\s+/g, "-")))],
      }),
    });
    setNotice("Saved as a private study note.");
  };

  const saveAnnotation = async () => {
    if (!selectedVerse) return;
    if (!user) return onAuth();
    const existing = annotations.find((item) => item.verseStart === selectedVerse);
    await api("/api/annotations", {
      method: "PUT",
      body: JSON.stringify({
        translation: activeVersionId,
        book: bibleBooks[bookIndex],
        chapter: chapterNumber,
        verseStart: selectedVerse,
        verseEnd: selectedVerse,
        highlightColor: existing?.highlightColor ?? null,
        note: note || null,
        tags: tags.split(",").map((tag) => tag.trim()).filter(Boolean),
        notebookId: existing?.notebookId ?? null,
        isBookmarked: Boolean(existing?.isBookmarked),
      }),
    });
    const response = await api<{ annotations: Annotation[] }>(
      `/api/annotations?book=${encodeURIComponent(bibleBooks[bookIndex]!)}&chapter=${chapterNumber}&translation=${activeVersionId}`,
    );
    setAnnotations(response.annotations);
    setNotice("Verse note saved.");
  };

  const toggleCurrentBookmark = async (targetVerse = readingVerse) => {
    if (!user) return onAuth();
    const verse = targetVerse;
    const bookmarkAtTarget =
      bookmarks.find((bookmark) =>
        bookmarkMatchesLocation(bookmark, {
          translation: activeVersionId,
          book: bibleBooks[bookIndex]!,
          chapter: chapterNumber,
          verse,
        }),
      ) ?? null;
    const existing = annotations.find(
      (item) => item.verseStart <= verse && item.verseEnd >= verse,
    );
    const existingTags = (() => {
      try {
        const parsed = JSON.parse(existing?.tagsJson ?? "[]");
        return Array.isArray(parsed)
          ? parsed.filter((tag): tag is string => typeof tag === "string")
          : [];
      } catch {
        return [];
      }
    })();
    await api("/api/annotations", {
      method: "PUT",
      body: JSON.stringify({
        translation: activeVersionId,
        book: bibleBooks[bookIndex],
        chapter: chapterNumber,
        verseStart: verse,
        verseEnd: verse,
        highlightColor: existing?.highlightColor ?? null,
        note: existing?.note ?? null,
        tags: existingTags,
        notebookId: existing?.notebookId ?? null,
        isBookmarked: !bookmarkAtTarget,
      }),
    });
    const [annotationResponse, bookmarkResponse] = await Promise.all([
      api<{ annotations: Annotation[] }>(
        `/api/annotations?book=${encodeURIComponent(bibleBooks[bookIndex]!)}&chapter=${chapterNumber}&translation=${activeVersionId}`,
      ),
      api<{ bookmarks: ScriptureBookmark[] }>("/api/bookmarks"),
    ]);
    setAnnotations(annotationResponse.annotations);
    setBookmarks(bookmarkResponse.bookmarks);
    setNotice(
      bookmarkAtTarget
        ? `Removed bookmark from ${bibleBooks[bookIndex]} ${chapterNumber}:${verse}.`
        : `Bookmarked ${bibleBooks[bookIndex]} ${chapterNumber}:${verse}.`,
    );
  };

  const removeBookmark = async (bookmark: ScriptureBookmark) => {
    await api(`/api/bookmarks/${encodeURIComponent(bookmark.id)}`, { method: "DELETE" });
    setBookmarks((current) => current.filter((item) => item.id !== bookmark.id));
    if (
      bookmark.translation === activeVersionId &&
      bookmark.book === bibleBooks[bookIndex] &&
      bookmark.chapter === chapterNumber
    ) {
      const response = await api<{ annotations: Annotation[] }>(
        `/api/annotations?book=${encodeURIComponent(bibleBooks[bookIndex]!)}&chapter=${chapterNumber}&translation=${activeVersionId}`,
      );
      setAnnotations(response.annotations);
    }
    setNotice(`Removed bookmark from ${bookmark.book} ${bookmark.chapter}:${bookmark.verseStart}.`);
  };

  const handleReaderScroll = (event: ReactUIEvent<HTMLDivElement>) => {
    if (audioPlaying) setFollowText(false);
    const scroller = event.currentTarget;
    const readingLine = scroller.getBoundingClientRect().top + 72;
    const visibleVerse = [...scroller.querySelectorAll<HTMLElement>(".verse")].find(
      (element) => element.getBoundingClientRect().bottom >= readingLine,
    );
    const verse = Number(visibleVerse?.id.replace("verse-", ""));
    if (Number.isInteger(verse) && verse > 0 && verse !== readingVerse) {
      setReadingVerse(verse);
    }
  };

  const goToReference = (event: FormEvent) => {
    event.preventDefault();
    const reference = parseReference(search);
    if (reference) {
      navigate(referencePath(search));
      setSearch("");
      return;
    }
    const results = searchPassageSections(passageSections, search);
    if (!results.length) {
      setLoadError("No matching passage guide was found. Try a reference or a story such as Nicodemus.");
      return;
    }
    setLoadError("");
    setPassageGuideQuery(search.trim());
    setPassageGuideResults(results);
    setSearch("");
    setMobileSearchOpen(false);
  };

  const stopPlayback = () => {
    continueAcrossChaptersRef.current = false;
    pendingContinueRef.current = false;
    audioRef.current?.pause();
    audioRef.current = null;
    window.speechSynthesis?.cancel();
    utteranceRef.current = null;
    setAudioPlaying(false);
    setAudioPreparing(false);
    setActiveVerse(null);
    setActiveWord(null);
    setAudioElapsed(0);
  };

  const advanceChapter = () => {
    if (!continueAcrossChaptersRef.current) return stopPlayback();
    const next = nextChapterReference(bookIndex, chapterNumber, readerStructure);
    if (!next) return stopPlayback();
    pendingContinueRef.current = true;
    navigate(`/scripture/${encodeURIComponent(bibleBooks[next.bookIndex]!)}/${next.chapter}`);
  };

  const playCachedSegment = (manifest: BibleAudioManifest, index: number) => {
    const segment = manifest.segments[index];
    if (!segment) return advanceChapter();
    audioSegmentRef.current = index;
    const audio = new Audio(segment.audioUrl);
    audio.playbackRate = audioSpeed;
    audioRef.current = audio;
    setActiveVerse(segment.verseStart);
    audio.ontimeupdate = () => {
      const elapsedMs = audio.currentTime * 1000;
      setAudioElapsed(audio.currentTime);
      const timing = [...segment.words].reverse().find((word) => word.startMs <= elapsedMs);
      if (timing) {
        setActiveWord(timing);
        setActiveVerse(timing.verse);
      }
    };
    audio.onended = () => playCachedSegment(manifest, index + 1);
    audio.onerror = () => {
      setNotice("Recorded audio was interrupted. Continuing with this device's offline voice.");
      startSpeechFallback();
    };
    void audio.play().then(() => setAudioPlaying(true)).catch(() => startSpeechFallback());
  };

  const speakQueue = () => {
    const item = speechQueueRef.current[speechIndexRef.current];
    if (!item) return advanceChapter();
    const utterance = new SpeechSynthesisUtterance(item.text);
    utterance.rate = audioSpeed;
    utterance.onstart = () => {
      const playbackLocation = playbackLocationRef.current;
      if (playbackLocation.bookIndex !== item.bookIndex || playbackLocation.chapter !== item.chapter) {
        playbackLocationRef.current = { bookIndex: item.bookIndex, chapter: item.chapter };
        navigate(`/scripture/${encodeURIComponent(item.book)}/${item.chapter}`);
      }
      setActiveVerse(item.verse);
      setAudioPlaying(true);
    };
    utterance.onend = () => {
      speechIndexRef.current += 1;
      speakQueue();
    };
    utterance.onerror = () => stopPlayback();
    utteranceRef.current = utterance;
    window.speechSynthesis.speak(utterance);
  };

  const startSpeechFallback = (selectedOnly = false) => {
    if (!("speechSynthesis" in window)) {
      setLoadError("Audio reading is not available in this browser.");
      return;
    }
    window.speechSynthesis.cancel();
    const selectedVerses = new Set<number>();
    if (selectedOnly) {
      for (const range of ranges) {
        if (range.bookIndex !== bookIndex || range.chapter !== chapterNumber) continue;
        for (let verse = range.verseStart; verse <= range.verseEnd; verse += 1) selectedVerses.add(verse);
      }
    }
    speechQueueRef.current = chapter
      .map((text, index) => ({
        book: bibleBooks[bookIndex]!,
        bookIndex,
        chapter: chapterNumber,
        verse: index + 1,
        text: readableBibleText(text, activeVersionId),
      }))
      .filter((item) => item.text && (!selectedOnly || selectedVerses.has(item.verse)));
    speechIndexRef.current = 0;
    playbackLocationRef.current = { bookIndex, chapter: chapterNumber };
    continueAcrossChaptersRef.current = !selectedOnly;
    speakQueue();
  };

  const startSelectedPlayback = async () => {
    if (!ranges.length) return;
    if (!("speechSynthesis" in window)) {
      setLoadError("Selected-passage playback is not available in this browser.");
      return;
    }
    setAudioPreparing(true);
    try {
      const chapters = new Map<string, string[]>();
      const queue: Array<{
        book: string;
        bookIndex: number;
        chapter: number;
        verse: number;
        text: string;
      }> = [];
      for (const range of ranges) {
        const key = `${range.bookIndex}:${range.chapter}`;
        let source = chapters.get(key);
        if (!source) {
          source = await resolveRangeVerses(range);
          chapters.set(key, source);
        }
        for (let verse = range.verseStart; verse <= range.verseEnd; verse += 1) {
          const text = source[verse - 1];
          if (text) queue.push({
            book: range.book,
            bookIndex: range.bookIndex,
            chapter: range.chapter,
            verse,
            text: readableBibleText(text, activeVersionId),
          });
        }
      }
      stopPlayback();
      continueAcrossChaptersRef.current = false;
      playbackLocationRef.current = { bookIndex, chapter: chapterNumber };
      speechQueueRef.current = queue;
      speechIndexRef.current = 0;
      speakQueue();
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : "The selected passages could not be prepared.");
    } finally {
      setAudioPreparing(false);
    }
  };

  async function startPlayback(continuing = false, selectedOnly = false) {
    if (audioPlaying) {
      if (audioRef.current) {
        audioRef.current.pause();
        setAudioPlaying(false);
      } else {
        window.speechSynthesis.pause();
        setAudioPlaying(false);
      }
      return;
    }
    if (audioRef.current?.paused) {
      audioRef.current.playbackRate = audioSpeed;
      await audioRef.current.play();
      setAudioPlaying(true);
      return;
    }
    if (window.speechSynthesis?.paused) {
      window.speechSynthesis.resume();
      setAudioPlaying(true);
      return;
    }
    if (!chapter.length) return;
    continueAcrossChaptersRef.current = !selectedOnly;
    if (selectedOnly) return startSpeechFallback(true);
    setAudioPreparing(true);
    try {
      let manifest = await api<BibleAudioManifest>(
        `/api/bible/audio/${activeVersionId}/${encodeURIComponent(bibleBooks[bookIndex]!)}/${chapterNumber}`,
      );
      if (manifest.segments.length) {
        setAudioManifest(manifest);
        setAudioPreparing(false);
        playCachedSegment(manifest, 0);
        return;
      }
      if (user && !continuing) {
        await api("/api/bible/audio/prepare", {
          method: "POST",
          body: JSON.stringify({
            versionId: activeVersionId,
            editionHash: activeVersion.editionHash,
            book: bibleBooks[bookIndex],
            chapter: chapterNumber,
          }),
        }).catch(() => undefined);
        manifest = await api<BibleAudioManifest>(
          `/api/bible/audio/${activeVersionId}/${encodeURIComponent(bibleBooks[bookIndex]!)}/${chapterNumber}`,
        );
        setAudioManifest(manifest);
      }
      setNotice("Using this device's offline voice while recorded audio is prepared and globally cached.");
      startSpeechFallback();
    } catch {
      startSpeechFallback();
    } finally {
      setAudioPreparing(false);
    }
  }

  const changeSpeed = () => {
    const index = AUDIO_SPEEDS.indexOf(audioSpeed);
    const next = AUDIO_SPEEDS[(index + 1) % AUDIO_SPEEDS.length]!;
    setAudioSpeed(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
    if (utteranceRef.current) utteranceRef.current.rate = next;
  };

  const chapterWordState = (
    verse: number,
    tokenIndex: number,
    token: string,
    readableText: string,
  ) => {
    if (!activeWord || activeWord.verse !== verse) return false;
    const normalized = token.toLowerCase().replace(/[^\p{L}\p{N}']/gu, "");
    const target = activeWord.word.toLowerCase().replace(/[^\p{L}\p{N}']/gu, "");
    if (normalized !== target) return false;
    const wordsBefore = readableText
      ?.split(/\s+/)
      .slice(0, tokenIndex)
      .filter((candidate) => candidate.toLowerCase().replace(/[^\p{L}\p{N}']/gu, "") === target)
      .length ?? 0;
    const timedWordsBefore = (audioManifest?.segments ?? [])
      .flatMap((segment: BibleAudioSegment) => segment.words)
      .filter((word) => word.verse === verse && word.startMs < activeWord.startMs && word.word.toLowerCase().replace(/[^\p{L}\p{N}']/gu, "") === target)
      .length;
    return wordsBefore === timedWordsBefore;
  };

  return (
    <div className="view scripture-view">
      <header className="page-header">
        <div>
          <h1 className="page-title">Scripture</h1>
          <p className="page-subtitle">Read, listen, select across the Bible, save notes, and study with Sola.</p>
        </div>
        <button className="btn" type="button" onClick={() => setBooksOpen(open=>!open)}><BookOpenText size={16}/>{bibleBooks[bookIndex]} {chapterNumber}</button>
        <button className="btn" type="button" onClick={openVersionPicker}>
          <BookOpenText size={16} /> {activeVersion.abbreviation} <CaretDown size={14} />
        </button>
      </header>

      <div className={`bible-layout ${studyOpen || selectedVerse ? "research-open" : ""}`}>
        <aside className={`card bible-nav ${booksOpen ? "mobile-books-open" : ""}`}>
          <div className="bible-nav-head">
            <strong className="bible-nav-title"><BookOpenText size={16} /> 66 books</strong>
            <button type="button" className="bible-books-toggle" onClick={() => setBooksOpen((open) => !open)}>
              <span>{bibleBooks[bookIndex]}</span><span className="muted">{booksOpen ? "Close" : "Books"}</span>
            </button>
          </div>
          <div className="book-list">
            <div className="testament-label">Old Testament</div>
            {bibleBooks.slice(0, 39).map((book, index) => (
              <Link
                className={`book-btn ${index === bookIndex ? "active" : ""} ${bookmarkedBookNames.has(book) ? "has-bookmark" : ""}`}
                to={`/scripture/${encodeURIComponent(book)}/1`}
                aria-current={index === bookIndex ? "page" : undefined}
                aria-label={`${bookOptionLabel(book, readerStructure?.[index]?.length ?? 0)}${bookmarkedBookNames.has(book) ? ", contains a bookmark" : ""}`}
                key={book}
              >
                <span>{book}</span><small className="chap-count">{readerStructure?.[index]?.length ?? ""}</small>
              </Link>
            ))}
            <div className="testament-label">New Testament</div>
            {bibleBooks.slice(39).map((book, localIndex) => {
              const index = localIndex + 39;
              return (
                <Link
                  className={`book-btn ${index === bookIndex ? "active" : ""} ${bookmarkedBookNames.has(book) ? "has-bookmark" : ""}`}
                  to={`/scripture/${encodeURIComponent(book)}/1`}
                  aria-current={index === bookIndex ? "page" : undefined}
                  aria-label={`${bookOptionLabel(book, readerStructure?.[index]?.length ?? 0)}${bookmarkedBookNames.has(book) ? ", contains a bookmark" : ""}`}
                  key={book}
                >
                  <span>{book}</span><small className="chap-count">{readerStructure?.[index]?.length ?? ""}</small>
                </Link>
              );
            })}
          </div>
          <div className="chapter-grid">
            {Array.from({ length: maxChapter }, (_, index) => index + 1).map((chapterValue) => (
              <Link
                className={`chapter-btn ${chapterValue === chapterNumber ? "active" : ""} ${bookmarkedChapterNumbers.has(chapterValue) ? "has-bookmark" : ""}`}
                to={`/scripture/${encodeURIComponent(bibleBooks[bookIndex]!)}/${chapterValue}`}
                aria-label={`Chapter ${chapterValue}${bookmarkedChapterNumbers.has(chapterValue) ? ", bookmarked" : ""}`}
                key={chapterValue}
              >
                {chapterValue}
              </Link>
            ))}
          </div>
        </aside>

        <article className="card bible-reader">
          <div className="reader-mobile-controls">
            <button type="button" className="reader-icon-button" onClick={onOpenNavigation} aria-label="Open navigation">
              <List size={23} />
            </button>
            <div className="row">
              <button type="button" className={`reader-icon-button ${audioPlaying ? "active" : ""}`} onClick={() => void startPlayback()} aria-label="Listen to Scripture">
                {audioPlaying ? <Pause size={21} weight="fill" /> : <SpeakerHigh size={21} />}
              </button>
              <button type="button" className={`reader-icon-button ${mobileSearchOpen ? "active" : ""}`} onClick={() => setMobileSearchOpen((open) => !open)} aria-label="Search Scripture">
                <MagnifyingGlass size={21} />
              </button>
              <button type="button" className="reader-icon-button" disabled={!ranges.length} onClick={() => setQuestionOpen(true)} aria-label="Ask Sola about selected Scripture">
                <Sparkle size={20} />
              </button>
              <button
                type="button"
                className={`reader-icon-button ${bookmarksOpen || currentBookmark ? "active" : ""}`}
                onClick={() => setBookmarksOpen(true)}
                aria-label={currentBookmark ? `Bookmarked at ${bibleBooks[bookIndex]} ${chapterNumber}:${readingVerse}. Open bookmarks` : "Open Scripture bookmarks"}
              >
                <BookmarkSimple size={20} weight={currentBookmark ? "fill" : "regular"} />
              </button>
              <button type="button" className="reader-translation-pill" onClick={openVersionPicker} aria-label={`Choose Bible version. Current version ${activeVersion.name}`}>
                {activeVersion.abbreviation}
              </button>
            </div>
          </div>

          {mobileSearchOpen ? (
            <form className="reader-mobile-search" onSubmit={goToReference}>
              <input className="input" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Reference or story, such as Nicodemus" autoFocus />
              <button className="reader-icon-button" aria-label="Open reference"><ArrowRight size={19} /></button>
            </form>
          ) : null}

          <div className="bible-toolbar">
            <button className="select bible-version-button" type="button" onClick={openVersionPicker}>
              {activeVersion.abbreviation} · {activeVersion.name} <CaretDown size={14} />
            </button>
            <form className="bible-search" onSubmit={goToReference}>
              <input className="input" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Reference or story, such as Nicodemus" />
              <button className="btn icon-btn small-btn" aria-label="Open reference"><MagnifyingGlass size={16} /></button>
            </form>
            <button className="btn" type="button" onClick={() => void startPlayback()}>
              {audioPlaying ? <Pause size={16} /> : <SpeakerHigh size={16} />} Listen
            </button>
            <button className="btn" type="button" onClick={() => setBookmarksOpen(true)}>
              <BookmarkSimple size={16} weight={currentBookmark ? "fill" : "regular"} /> Bookmarks
              {bookmarks.length ? <span className="bookmark-count">{bookmarks.length}</span> : null}
            </button>
          </div>

          {notice ? <div className="reader-notice" role="status">{notice}<button type="button" onClick={() => setNotice("")}><X size={14} /></button></div> : null}

          <div ref={readerScrollRef} className="bible-scroll" onScroll={handleReaderScroll}>
            <div className="chapter-heading">
              <h2 className="book-name"><span>{bibleBooks[bookIndex]}</span><strong>{chapterNumber}</strong></h2>
              <div className="translation">{activeVersion.name} · {activeVersion.license}</div>
              {activeVersionId === "kjv" ? (
                <p className="reader-editorial-key">
                  <em>Italic words</em> were supplied by the KJV translators. <Info size={12} /> opens an editorial note.
                </p>
              ) : null}
            </div>
            {loadError ? (
              <div className="empty-state"><WarningCircle size={26} /><h3>Chapter unavailable</h3><p>{loadError}</p></div>
            ) : !chapter.length ? (
              <div className="bible-loading"><div className="thinking"><span /><span /><span /></div><p>Opening {activeVersion.abbreviation}…</p></div>
            ) : (
              <div className={`bible-text ${selectionMode ? "selection-mode" : ""}`}>
                {chapter.map((text, index) => {
                  const verse = index + 1;
                  const verseLabel = activeVersion.verseLabels?.[`${bookIndex}:${chapterNumber}:${verse}`] ?? verse;
                  if (!text) return null;
                  const parsedVerse =
                    activeVersionId === "kjv"
                      ? parseKjvEditorialVerse(text)
                      : {
                          segments: [{ kind: "text" as const, text }],
                          notes: [],
                          readableText: text,
                        };
                  const annotation = annotations.find((item) => item.verseStart === verse);
                  const isSelected = scriptureRangeContains(ranges, bookIndex, chapterNumber, verse);
                  const headings = sectionsByVerse.get(verse) ?? [];
                  let tokenIndex = 0;
                  return (
                    <span className="verse-flow" key={verse}>
                      {headings.map((section) => (
                        <span className={`passage-section-heading level-${section.level}`} key={section.id}>
                          <span className="passage-section-eyebrow">Editorial passage guide</span>
                          <strong>{section.title}</strong>
                          <small>{passageSectionLabel(section)}</small>
                        </span>
                      ))}
                      <span className="verse-unit">
                        <button
                          id={`verse-${verse}`}
                          className={[
                            "verse",
                            selectedVerse === verse ? "selected" : "",
                            isSelected ? "multi-selected" : "",
                            activeVerse === verse ? "audio-active" : "",
                            annotation?.highlightColor ? `hl-${annotation.highlightColor}` : "",
                            annotation?.note ? "has-note" : "",
                          ].join(" ")}
                          onPointerDown={() => pointerDown(verse)}
                          onPointerUp={(event) => verseClick(verse, event)}
                          onPointerCancel={cancelLongPress}
                          onPointerLeave={cancelLongPress}
                          onContextMenu={(event) => event.preventDefault()}
                          aria-pressed={isSelected}
                          aria-label={`${bibleBooks[bookIndex]} ${chapterNumber}:${verseLabel}. ${parsedVerse.readableText}`}
                        >
                          <sup className="verse-num">{verseLabel}</sup>
                          {parsedVerse.segments.map((segment, segmentIndex) => (
                            <span className={segment.kind === "supplied" ? "kjv-supplied-word" : undefined} key={`${segmentIndex}-${segment.kind}`}>
                              {segment.text.split(/(\s+)/).map((token) => {
                                const currentTokenIndex = tokenIndex;
                                tokenIndex += 1;
                                return (
                                  <span
                                    className={chapterWordState(verse, currentTokenIndex, token, parsedVerse.readableText) ? "audio-word-active" : ""}
                                    key={`${currentTokenIndex}-${token}`}
                                  >
                                    {token}
                                  </span>
                                );
                              })}
                            </span>
                          ))}{" "}
                        </button>
                        {parsedVerse.notes.length ? (
                          <button
                            type="button"
                            className="kjv-editorial-note-trigger"
                            onClick={() => setEditorialNote({
                              reference: `${bibleBooks[bookIndex]} ${chapterNumber}:${verse}`,
                              notes: parsedVerse.notes,
                            })}
                            aria-label={`Open KJV editorial note for ${bibleBooks[bookIndex]} ${chapterNumber}:${verse}`}
                          >
                            <Info size={12} weight="bold" />
                          </button>
                        ) : null}
                      </span>
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          {ranges.length ? (
            <div className="scripture-selection-tray" aria-live="polite">
              <div className="selection-summary">
                <strong>{selectionCount} verse{selectionCount === 1 ? "" : "s"} selected</strong>
                <span>{ranges.slice(0, 2).map(scriptureRangeLabel).join(", ")}{ranges.length > 2 ? ` +${ranges.length - 2}` : ""}</span>
              </div>
              <div className="selection-actions">
                <button type="button" onClick={() => void startSelectedPlayback()}><SpeakerHigh size={16} /> Listen</button>
                <button type="button" onClick={() => setQuestionOpen(true)}><Sparkle size={16} /> Ask Sola</button>
                <button type="button" onClick={() => void saveSelectionNote()}><NotePencil size={16} /> Save note</button>
                <button type="button" onClick={clearSelection} aria-label="Clear selection"><X size={16} /></button>
              </div>
            </div>
          ) : null}

          <footer className="reader-mobile-footer">
            <button type="button" className={`reader-play-button ${audioPlaying ? "active" : ""}`} onClick={() => void startPlayback()} aria-label={audioPlaying ? "Pause Scripture audio" : "Play Scripture audio"}>
              {audioPreparing ? <span className="spinner" /> : audioPlaying ? <Pause size={22} weight="fill" /> : <Play size={22} weight="fill" />}
            </button>
            <div className="reader-chapter-switcher">
              {previousChapter ? (
                <Link
                  aria-label={`Previous chapter, ${bibleBooks[previousChapter.bookIndex]} ${previousChapter.chapter}`}
                  to={`/scripture/${encodeURIComponent(bibleBooks[previousChapter.bookIndex]!)}/${previousChapter.chapter}`}
                >
                  <ArrowLeft size={21} />
                </Link>
              ) : <span className="disabled" aria-hidden="true"><ArrowLeft size={21} /></span>}
              <button type="button" onClick={() => setBooksOpen(true)}>{bibleBooks[bookIndex]} {chapterNumber}</button>
              {nextChapter ? (
                <Link
                  aria-label={`Next chapter, ${bibleBooks[nextChapter.bookIndex]} ${nextChapter.chapter}`}
                  to={`/scripture/${encodeURIComponent(bibleBooks[nextChapter.bookIndex]!)}/${nextChapter.chapter}`}
                >
                  <ArrowRight size={21} />
                </Link>
              ) : <span className="disabled" aria-hidden="true"><ArrowRight size={21} /></span>}
            </div>
          </footer>

          {(audioPlaying || activeVerse != null) ? (
            <div className="scripture-audio-player" role="region" aria-label="Scripture audio player">
              <button type="button" onClick={() => void startPlayback()} aria-label={audioPlaying ? "Pause" : "Play"}>
                {audioPlaying ? <Pause size={17} /> : <Play size={17} />}
              </button>
              <button type="button" onClick={stopPlayback} aria-label="Stop"><Stop size={16} weight="fill" /></button>
              <span><strong>{bibleBooks[bookIndex]} {chapterNumber}{activeVerse ? `:${activeVerse}` : ""}</strong><small>{Math.floor(audioElapsed / 60)}:{String(Math.floor(audioElapsed % 60)).padStart(2, "0")}</small></span>
              <button type="button" onClick={changeSpeed}>{audioSpeed}×</button>
              <button type="button" className={followText ? "active" : ""} onClick={() => setFollowText((value) => !value)}>Follow text</button>
            </div>
          ) : null}
        </article>

        <button type="button" className={`bible-ai-backdrop ${studyOpen ? "open" : ""}`} aria-label="Close study tools" onClick={() => setStudyOpen(false)} />
        <aside ref={studyPanelRef} role={compactStudy&&studyOpen?"dialog":"complementary"} aria-modal={compactStudy&&studyOpen?true:undefined} aria-label="Study passage" className={`card bible-ai ${studyOpen ? "mobile-study-open" : ""}`}>
          <div className="bible-ai-head">
            <div className="row spread"><strong>Study with Sola</strong><button className="top-icon bible-ai-close" aria-label="Close study" type="button" onClick={() => setStudyOpen(false)}><X size={17} /></button></div>
          </div>
          <div className="bible-ai-content">
            <ReaderStudy key={JSON.stringify([user?.id,activeVersionId,ranges,selectedVerse,bookIndex,chapterNumber])} userId={user?.id??null} onAuth={onAuth}
              selections={ranges.length || selectedVerse ? [{versionId:activeVersionId,editionHash:activeVersion.editionHash,ranges:ranges.length?ranges:[{book:bibleBooks[bookIndex]!,bookIndex,chapter:chapterNumber,verseStart:selectedVerse!,verseEnd:selectedVerse!}]}] : []}>

            {selectedVerse ? (
              <div className="stack">
                <div className="selected-scripture">
                  <div className="scripture-label">Direct Scripture · {activeVersion.abbreviation}</div>
                  <blockquote><sup>{selectedVerse}</sup> {readableBibleText(chapter[selectedVerse - 1] ?? "", activeVersionId)}</blockquote>
                  <strong>{bibleBooks[bookIndex]} {chapterNumber}:{selectedVerse}</strong>
                </div>
                <details className="reader-private-notes"><summary>Notes and bookmarks</summary>
                <label className="field">Your private note<textarea className="textarea compact" value={note} onChange={(event) => setNote(event.target.value)} /></label>
                <label className="field">Tags<input className="input" value={tags} onChange={(event) => setTags(event.target.value)} placeholder="promise, gospel, memorize" /></label>
                <button className="btn" type="button" onClick={() => void toggleCurrentBookmark(selectedVerse)}>
                  <BookmarkSimple size={16} weight={bookmarks.some((bookmark) =>
                    bookmarkMatchesLocation(bookmark, {
                      translation: activeVersionId,
                      book: bibleBooks[bookIndex]!,
                      chapter: chapterNumber,
                      verse: selectedVerse,
                    }),
                  ) ? "fill" : "regular"} />
                  {bookmarks.some((bookmark) =>
                    bookmarkMatchesLocation(bookmark, {
                      translation: activeVersionId,
                      book: bibleBooks[bookIndex]!,
                      chapter: chapterNumber,
                      verse: selectedVerse,
                    }),
                  ) ? "Remove bookmark" : "Bookmark this verse"}
                </button>
                <button className="btn" type="button" onClick={() => void saveAnnotation()}><NotePencil size={16} /> Save verse note</button>
                </details>

              </div>
            ) : <p className="muted">Select a verse to study its wording and sources.</p>}
            </ReaderStudy>
          </div>
        </aside>
      </div>

      {bookmarksOpen ? (
        <div className="modal-backdrop open" role="presentation" onMouseDown={() => setBookmarksOpen(false)}>
          <section
            className="modal-card reader-bookmarks-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="reader-bookmarks-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="modal-header">
              <div>
                <span className="eyebrow">Your reading places</span>
                <h2 id="reader-bookmarks-title">Scripture bookmarks</h2>
              </div>
              <button className="top-icon" type="button" onClick={() => setBookmarksOpen(false)} aria-label="Close bookmarks">
                <X size={18} />
              </button>
            </div>
            <div className="reader-bookmark-current">
              <span>
                <small>Current reading position</small>
                <strong>{bibleBooks[bookIndex]} {chapterNumber}:{readingVerse}</strong>
                <small>{activeVersion.abbreviation}</small>
              </span>
              <button className={`btn ${currentBookmark ? "" : "primary"}`} type="button" onClick={() => void toggleCurrentBookmark()}>
                <BookmarkSimple size={16} weight={currentBookmark ? "fill" : "regular"} />
                {currentBookmark ? "Unbookmark" : "Bookmark"}
              </button>
            </div>
            <p className="reader-bookmark-legend">
              <span className="bookmark-corner-sample" aria-hidden="true" />
              Red corners identify books and chapters containing a bookmark.
            </p>
            {!user ? (
              <div className="empty-state compact">
                <BookmarkSimple size={25} />
                <h3>Sync your reading places</h3>
                <p>Sign in to save bookmarks across your devices.</p>
                <button className="btn primary" type="button" onClick={onAuth}>Sign in</button>
              </div>
            ) : bookmarks.length ? (
              <div className="reader-bookmark-list">
                {bookmarks.map((bookmark) => (
                  <div className="reader-bookmark-row" key={bookmark.id}>
                    <Link to={scriptureBookmarkPath(bookmark)} onClick={() => setBookmarksOpen(false)}>
                      <BookmarkSimple size={17} weight="fill" />
                      <span>
                        <strong>{bookmark.book} {bookmark.chapter}:{bookmark.verseStart}{bookmark.verseEnd !== bookmark.verseStart ? `–${bookmark.verseEnd}` : ""}</strong>
                        <small>{bibleVersion(bookmark.translation).abbreviation} · saved {new Date(bookmark.updatedAt * 1000).toLocaleDateString()}</small>
                      </span>
                    </Link>
                    <button className="top-icon" type="button" onClick={() => void removeBookmark(bookmark)} aria-label={`Remove bookmark ${bookmark.book} ${bookmark.chapter}:${bookmark.verseStart}`}>
                      <Trash size={16} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state compact">
                <BookmarkSimple size={25} />
                <h3>No bookmarks yet</h3>
                <p>Save your current verse, then return here whenever you are ready to continue.</p>
              </div>
            )}
          </section>
        </div>
      ) : null}

      {versionPickerVisible ? (
        <div className="modal-backdrop open" role="presentation" onMouseDown={closeVersionPicker}>
          <section className="modal-card reader-version-picker" role="dialog" aria-modal="true" aria-labelledby="version-picker-title" onMouseDown={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <div><span className="eyebrow">Bible editions</span><h2 id="version-picker-title">Choose a version</h2></div>
              <button className="top-icon" type="button" onClick={closeVersionPicker} aria-label="Close"><X size={18} /></button>
            </div>
            <p className="muted">KJV is included by default. Download any other edition for offline reading.</p>
            <div className="reader-version-list">
              {versions.map((version) => {
                const isInstalled = installed.includes(version.id);
                const progress = downloadProgress[version.id];
                return (
                  <article className={`reader-version-row ${activeVersionId === version.id ? "active" : ""}`} key={version.id}>
                    <div>
                      <div className="row"><strong>{version.abbreviation}</strong><span>{version.name}</span>{version.id === preferredVersionId ? <span className="pill">Default</span> : null}</div>
                      <small>{versionAvailabilityText(version, isInstalled)}{version.available === false ? "" : ` · ${version.audio === "public-domain" ? "Human audio" : "Cached narration"}`}</small>
                      <small>{version.attribution}</small>
                      {progress != null ? <span className="progress"><span style={{ width: `${progress}%` }} /></span> : null}
                    </div>
                    <div className="reader-version-actions">
                      {activeVersionId === version.id ? <span className="pill green"><Check size={13} /> In use</span> : (
                        <button className="btn primary" type="button" disabled={progress != null || version.available === false} onClick={() => void switchVersion(version)}>
                          {version.available === false
                            ? "Unavailable"
                            : version.downloadable && !isInstalled
                              ? <><DownloadSimple size={15} /> Download</>
                              : "Use"}
                        </button>
                      )}
                      {version.downloadable && isInstalled ? <button className="top-icon" type="button" onClick={() => void removeVersion(version)} aria-label={`Remove ${version.name}`}><Trash size={15} /></button> : null}
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        </div>
      ) : null}

      {questionOpen ? (
        <div className="modal-backdrop open" role="presentation" onMouseDown={() => setQuestionOpen(false)}>
          <section className="modal-card scripture-question-sheet" role="dialog" aria-modal="true" aria-labelledby="scripture-question-title" onMouseDown={(event) => event.stopPropagation()}>
            <div className="modal-header"><div><span className="eyebrow">Selected Scripture</span><h2 id="scripture-question-title">Ask Sola</h2></div><button className="top-icon" type="button" onClick={() => setQuestionOpen(false)}><X size={18} /></button></div>
            <div className="selection-reference-list">{ranges.map((range) => <span className="pill" key={scriptureRangeLabel(range)}>{scriptureRangeLabel(range)}</span>)}</div>
            <textarea className="textarea" value={question} onChange={(event) => setQuestion(event.target.value)} placeholder="What would you like to ask about these passages?" />
            <div className="suggestion-grid compact">
              {QUESTION_SUGGESTIONS.map((suggestion) => <button type="button" onClick={() => setQuestion(suggestion)} key={suggestion}>{suggestion}</button>)}
            </div>
            <button className="btn primary wide" type="button" disabled={!question.trim()} onClick={() => askSola(question.trim())}><Sparkle size={16} /> Continue with Sola</button>
          </section>
        </div>
      ) : null}

      {passageGuideResults ? (
        <div className="modal-backdrop open" role="presentation" onMouseDown={() => setPassageGuideResults(null)}>
          <section className="modal-card passage-guide-dialog" role="dialog" aria-modal="true" aria-labelledby="passage-guide-title" onMouseDown={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <div><span className="eyebrow">Editorial passage guide</span><h2 id="passage-guide-title">Results for “{passageGuideQuery}”</h2></div>
              <button className="top-icon" type="button" onClick={() => setPassageGuideResults(null)} aria-label="Close passage guide"><X size={18} /></button>
            </div>
            <p className="muted">Section titles come from the public-domain Berean Standard Bible and remain distinct from Scripture.</p>
            <div className="passage-guide-results">
              {passageGuideResults.map((section) => {
                const preview = activeBible?.[section.bookIndex]?.[section.startChapter - 1]?.[section.startVerse - 1];
                return (
                  <button
                    type="button"
                    className="passage-guide-result"
                    key={section.id}
                    onClick={() => {
                      setPassageGuideResults(null);
                      navigate(`/scripture/${encodeURIComponent(section.book)}/${section.startChapter}?verse=${section.startVerse}`);
                    }}
                  >
                    <span className="passage-guide-icon"><MapTrifold size={18} /></span>
                    <span>
                      <strong>{section.title}</strong>
                      <small>{passageSectionLabel(section)}</small>
                      {preview ? <p>{readableBibleText(preview, activeVersionId)}</p> : null}
                    </span>
                    <ArrowRight size={17} />
                  </button>
                );
              })}
            </div>
            <small className="passage-guide-attribution">Editorial headings: Berean Standard Bible, public domain.</small>
          </section>
        </div>
      ) : null}

      {editorialNote ? (
        <div className="modal-backdrop open" role="presentation" onMouseDown={() => setEditorialNote(null)}>
          <section className="modal-card kjv-editorial-note-dialog" role="dialog" aria-modal="true" aria-labelledby="kjv-editorial-note-title" onMouseDown={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <div><span className="eyebrow">KJV editorial material</span><h2 id="kjv-editorial-note-title">{editorialNote.reference}</h2></div>
              <button className="top-icon" type="button" onClick={() => setEditorialNote(null)} aria-label="Close KJV editorial note"><X size={18} /></button>
            </div>
            <p className="muted">These notes are distinct from the direct Scripture text.</p>
            <div className="kjv-editorial-note-list">
              {editorialNote.notes.map((item, index) => (
                <div key={`${item.kind}-${index}`}>
                  <span>{item.kind === "subscription" ? "Traditional subscription" : "Translator note"}</span>
                  <p>{item.text}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}
